import { useEffect, useState } from 'react';
import Typography from '@components/common/typography';
import Input from '@components/common/input';
import Button from '@components/common/button';
import styles from './styles.module.scss';
import { routes } from '@const/routes';
import { useForm } from '@hooks/helpers/useForm';
import FormField from '@components/form_field';
import { zodResolver } from '@hookform/resolvers/zod';
import { signInValidation } from '@helpers/validation_objects/signin';
import { defaultSignInData } from '@const/default_form_data';
import { useLocation, useNavigate } from 'react-router-dom';
import { apiAuthService, apiUserService } from '@api';
import { useLocales } from '@hooks/helpers/useLocales';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { ETwoFAMethod, EUserRole, TTwoFALoginResult } from '@xeppt/xeppt-sdk/types';
import { useUserContext } from '@hooks/context/useUserContext';
import { useQueryParams } from '@hooks/helpers/useQueryParams';
import useSignupContext from '@hooks/context/useSignupContext';
import loginIllustration from '@svg/illustrations/auth/login.svg';
import loggedOut from '@svg/illustrations/auth/logged_out.svg';
import { showToast } from '@components/toast';
import MFALayout from '@layouts/auth/signin/mfa';
import passwordIllustration from '@svg/illustrations/auth/password.svg';

const SignInLayout = () => {
    const { t } = useTranslation('sign_in');
    const [uri] = useQueryParams(['uri']);
    const { validationLocale, labelLocale, requestErrorLocale, submitLocale, placeholderLocale } =
        useLocales();
    const navigate = useNavigate();
    const location = useLocation();
    const { handleLogout } = useUserContext();
    const { handleChangeIllustration } = useSignupContext();
    const [isLoggedOut, setIsLoggedOut] = useState(false);
    const [MFAData, setMFAData] = useState<TTwoFALoginResult>();
    const {
        control,
        formState: { isValid },
        getValues,
        reset
    } = useForm({
        defaultValues: defaultSignInData,
        resolver: zodResolver(
            signInValidation({
                messages: {
                    email: validationLocale('email'),
                    mfa: validationLocale('mfa')
                },
                isMFA: !!MFAData
            })
        )
    });

    const handleLogin = () => {
        apiUserService.profile().then(({ role }) => {
            if (role !== EUserRole.REGULAR_USER) {
                handleLogout();
                showToast.error(t('requests.error.role'));
            } else {
                if (location.state?.redirect) {
                    navigate(location.state?.redirect);
                } else {
                    navigate(routes.dashboard);
                }
            }
        });
    };

    const { handleRequest: handleVerify, isLoading: isVerifyLoading } = useApiMutation({
        method: () => {
            console.log({ sentMfaData: MFAData, code: getValues().mfaCode });
            return apiAuthService.loginVerify({
                token: MFAData?.token || '',
                code: getValues().mfaCode
            });
        },
        onSuccess: () => {
            handleLogin();
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleFallback } = useApiMutation({
        method: () => apiAuthService.fallbackTwoFA(MFAData?.token || ''),
        onSuccess: (data) => {
            console.log({ fallbackMfaData: data });
            setMFAData(data);
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleSubmitForm, isLoading } = useApiMutation({
        method: () => {
            const { email, password } = getValues();
            return apiAuthService.login({
                email_or_phone: email,
                password
            });
        },
        onSuccess: (data) => {
            if (uri) {
                apiAuthService
                    .authorize()
                    .then((data) => {
                        apiUserService
                            .registerExtension()
                            .then(() => window.location.replace(`${uri}?uuid=${data}`));
                    })
                    .catch();
            } else if (data && data.method !== ETwoFAMethod.NONE) {
                setMFAData(data);
            } else {
                handleLogin();
            }
        },
        onError: requestErrorLocale
    });

    useEffect(() => {
        if (location.state?.logged_out) {
            setIsLoggedOut(true);
            handleChangeIllustration(loggedOut);
        } else {
            setIsLoggedOut(false);
            handleChangeIllustration(loginIllustration);
        }
    }, [location]);

    useEffect(() => {
        if (MFAData) {
            handleChangeIllustration(passwordIllustration);
        }
    }, [MFAData]);

    if (MFAData) {
        return (
            <MFALayout
                method={MFAData.method}
                control={control}
                isLoading={isVerifyLoading}
                onSubmit={() => handleVerify(undefined)}
                onFallback={() => {
                    reset({
                        ...getValues(),
                        mfaCode: ''
                    });
                    setMFAData(undefined);
                    handleFallback(undefined);
                }}
            />
        );
    }

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <Typography className={styles.title} variant="h3">
                {isLoggedOut ? 'Your session timed out for security' : 'Welcome back'}
            </Typography>
            {isLoggedOut && (
                <Typography className={styles.description} variant="body2">
                    You were inactive, so your session timed out. Please sign in again
                </Typography>
            )}
            <div className={styles.form}>
                <FormField
                    //@ts-ignore
                    control={control}
                    name="email"
                    renderComponent={(props) => (
                        <Input
                            {...props}
                            full
                            placeholder={placeholderLocale('enter_email')}
                            label={labelLocale('email')}
                            name="email"
                        />
                    )}
                />
                <FormField
                    //@ts-ignore
                    control={control}
                    name="password"
                    renderComponent={(props) => (
                        <Input
                            {...props}
                            full
                            placeholder={placeholderLocale('enter_password')}
                            type="password"
                            name="password"
                            label={labelLocale('password')}
                        />
                    )}
                />
                <Button
                    variant="text-gray"
                    size="small"
                    onClick={() => navigate(routes.forgot_password)}>
                    {t('forgot')}
                </Button>
            </div>
            <Button
                onClick={handleSubmitForm}
                disabled={!isValid}
                isLoading={isLoading}
                variant="primary">
                {submitLocale('signin')}
            </Button>
        </motion.div>
    );
};
export default SignInLayout;
